import { useCallback, useContext, useEffect, useState } from 'react';
import AuthContext from '../components/shared/AuthContext';
import { useLearningRealtime } from '../realtime/LearningRealtimeProvider';

export default function useGamificationSummary({ autoLoad = true } = {}) {
  const realtime = useLearningRealtime();
  const { apiClient, isAuthenticated, isLoading: authLoading } = useContext(AuthContext);
  const [localSummary, setLocalSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (realtime?.refresh) {
      await realtime.refresh(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/api/gamification/summary');
      setLocalSummary(res.data);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      if (status === 401) setError('Sign in required for progress.');
      else if (status === 404) setError('Gamification API not found — restart or redeploy the backend.');
      else setError(msg || 'Progress unavailable — ensure the API is running the latest build.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, realtime]);

  useEffect(() => {
    if (!autoLoad || realtime?.gamification != null) return;
    if (!realtime && isAuthenticated && !authLoading) load();
  }, [autoLoad, realtime, isAuthenticated, authLoading, load]);

  if (realtime) {
    return {
      summary: realtime.gamification,
      loading: realtime.refreshing && !realtime.gamification,
      error: realtime.error,
      reload: () => realtime.refresh(false),
    };
  }

  return { summary: localSummary, loading, error, reload: load };
}
